import React, { useEffect, useState, useRef } from 'react';
import { ChevronRight, ChevronDown, Scale, ArrowUpRight, ArrowDownRight, FileDown } from 'lucide-react';
import { fetchFinancialYearComparison } from '../services/revenueService';
import { exportDashboardToPDF } from '../utils/exportUtils';
import { fetchSubcounties } from '../services/subcountyService';
import { useAuth } from '../context/useAuth';

const FinancialYearComparisonTable = () => {
  const [reportGroups, setReportGroups] = useState({});
  const [prevReportGroups, setPrevReportGroups] = useState({});
  const [subcounties, setSubcounties] = useState([]);
  const [isComparing, setIsComparing] = useState(true);
  const [expandedStreams, setExpandedStreams] = useState({});
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const dashboardRef = useRef(null);

  const now = new Date();
  const fyStartYear = (now.getMonth() + 1) >= 7 ? now.getFullYear() : now.getFullYear() - 1;
  const FY_MONTHS = [
    'July', 'August', 'September', 'October', 'November', 'December', 
    'January', 'February', 'March', 'April', 'May', 'June'
  ];

  const processGroups = (data) => {
    return data.reduce((acc, row) => {
      const stream = row.rev_stream || 'UNKNOWN';
      if (!acc[stream]) acc[stream] = [];
      acc[stream].push(row);
      return acc;
    }, {});
  };

  const calculateVariance = (current, previous) => {
    if (!previous || previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [scData, currentRes] = await Promise.all([
          fetchSubcounties(),
          fetchFinancialYearComparison(fyStartYear)
        ]);

        setSubcounties(scData);
        setReportGroups(processGroups(currentRes.data));
        
        if (isComparing) {
          const prevRes = await fetchFinancialYearComparison(fyStartYear - 1);
          setPrevReportGroups(processGroups(prevRes.data));
        }
      } catch (err) {
        console.error("Error loading comparison data:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [fyStartYear, isComparing]);

  const toggleStream = (streamName) => {
    setExpandedStreams(prev => ({ ...prev, [streamName]: !prev[streamName] }));
  };

  // Explicitly separate flattened data for grand totals
  const allStreamData = Object.values(reportGroups).flat();
  const prevStreamData = Object.values(prevReportGroups).flat();
  const totalCurrent = allStreamData.reduce((s, m) => s + m.total_county, 0);
  const totalPrevious = prevStreamData.reduce((s, m) => s + m.total_county, 0);
  const totalvariance = calculateVariance(totalCurrent, totalPrevious);

  if (loading) return <div className="p-10 text-center italic text-slate-500">Loading Financial Comparison...</div>;

  return (
    <div className="w-full bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
      <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50">
        <div>
          <h2 className="text-xl font-bold text-slate-800">Financial Year Revenue Analysis</h2>
          <p className="text-xs text-slate-500 font-medium">Comparing FY {fyStartYear}/{fyStartYear + 1} vs Previous Records</p>
        </div>
        <button 
          onClick={() => setIsComparing(!isComparing)}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-bold transition-all ${isComparing ? 'bg-blue-600 text-white' : 'bg-white text-blue-600 border border-blue-200'}`}
        >
          <Scale size={18} />
          {isComparing ? 'Hide Comparison' : 'Compare with Prev FY'}
        </button>
        <button 
                    onClick={() => exportDashboardToPDF(dashboardRef, `Revenue_DashBoard_FY${fyStartYear}`)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-slate-900 text-white text-[10px] font-black rounded-lg hover:bg-slate-800 active:scale-95 shadow-md"
                >
                    <FileDown size={14} /> EXPORT PDF
        </button>
      </div>

      <div ref={dashboardRef} className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead className="text-xs text-gray-700 uppercase bg-gray-100 border-b">
            <tr>
              <th className="px-6 py-4 font-bold sticky left-0 bg-gray-100 z-20 border-r">Revenue Stream</th>
              <th className="px-6 py-4 font-bold text-blue-800 text-center">Current FY ({fyStartYear}/{fyStartYear + 1})</th>
              {isComparing && (
                <>
                  <th className="px-6 py-4 font-bold text-purple-500 text-center">Prev FY ({fyStartYear - 1}/{fyStartYear})</th>
                  <th className="px-6 py-4 font-bold text-center">Variance %</th>
                </>
              )}
              {subcounties.map(sc => (
                <th key={sc.id} className="px-6 py-4 border-l text-center" title={`Officer: ${sc.username || 'N/A'}`}>
                  {sc.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-sm">
            {Object.entries(reportGroups).map(([streamName, months]) => {
              const isExpanded = expandedStreams[streamName];
              const currentTotal = months.reduce((sum, m) => sum + m.total_county, 0);
              
              // FIX: Specific lookup for previous year stream data
              const prevMonths = prevReportGroups[streamName] || [];
              const prevTotal = prevMonths.reduce((sum, m) => sum + m.total_county, 0);
              const variance = calculateVariance(currentTotal, prevTotal);

              return (
                <React.Fragment key={streamName}>
                  <tr className="border-b hover:bg-slate-50/80 transition-colors font-medium">
                    <td className="px-6 py-4 sticky left-0 bg-white z-10 border-r flex items-center gap-2 cursor-pointer" onClick={() => toggleStream(streamName)}>
                      {isExpanded ? <ChevronDown size={14} className="text-blue-500" /> : <ChevronRight size={14} className="text-slate-400" />}
                      <span className="break-words max-w-[200px]">{streamName}</span>
                    </td>
                    <td className="px-6 py-4 text-center font-bold text-blue-700">{currentTotal.toLocaleString()}</td>
                    
                    {isComparing && (
                      <>
                        <td className="px-6 py-4 text-center font-bold text-purple-500">{prevTotal.toLocaleString()}</td>
                        <td className={`px-6 py-4 text-center font-black ${variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          <div className="flex items-center justify-center gap-1">
                            {variance >= 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                            {Math.abs(variance).toFixed(1)}%
                          </div>
                        </td>
                      </>
                    )}

                    {subcounties.map(sc => (
                      <td key={sc.id} className="px-6 py-4 text-center border-l text-slate-600">
                        {months.reduce((sum, m) => sum + (m[sc.name] || 0), 0).toLocaleString()}
                      </td>
                    ))}
                  </tr>

                  {/* Monthly Breakdown Rows */}
                  {isExpanded && FY_MONTHS.map((monthName) => {
                  // 1. Find the record for this month in the current year data
                  const currentMonthData = months.find(m => m.calendar_month === monthName);
                  const currentAmount = currentMonthData ? currentMonthData.total_county : 0;

                  // 2. Find the record for this month in the previous year data
                  const prevMonths = prevReportGroups[streamName] || [];
                  const prevMonthData = prevMonths.find(pm => pm.calendar_month === monthName);
                  const prevAmount = prevMonthData ? prevMonthData.total_county : 0;

                  const mVar = calculateVariance(currentAmount, prevAmount);

                  return (
                    <tr key={monthName} className="bg-slate-50/50 border-b text-xs italic text-slate-500">
                      <td className="px-12 py-2 sticky left-0 bg-slate-50 border-r">
                        {monthName} {/* Displays month name even if amount is 0 */}
                      </td>
                      <td className="px-6 py-2 text-center font-medium">
                        {currentAmount.toLocaleString()}
                      </td>
                      
                      {isComparing && (
                        <>
                          <td className="px-6 py-2 text-center">
                            {prevAmount.toLocaleString()}
                          </td>
                          <td className={`px-6 py-2 text-center font-bold ${mVar >= 0 ? 'text-emerald-500' : 'text-red-400'}`}>
                            {mVar.toFixed(1)}%
                          </td>
                        </>
                      )}

                      {/* Sub-county mapping with 0 fallback */}
                      {subcounties.map(sc => (
                        <td key={sc.id} className="px-6 py-2 text-center border-l">
                          {(currentMonthData?.[sc.name] || 0).toLocaleString()}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                </React.Fragment>
              );
            })}

            {/* Grand Total Row */}
            <tr className="bg-blue-100 font-black border-t-2 border-blue-200 text-blue-900">
              <td className="px-6 py-5 sticky left-0 bg-blue-100 z-10 border-r uppercase tracking-wider text-sm">County Grand Total</td>
              <td className="px-6 py-5 text-center text-lg">
                {totalCurrent.toLocaleString()}
              </td>
              {isComparing && (
                <>
                  <td className="px-6 py-5 text-center text-lg text-purple-500">
                    {totalPrevious.toLocaleString()}
                  </td>
                  <td className={`px-6 py-5 text-center text-lg ${totalvariance >= 0 ? 'text-emerald-500' : 'text-red-400'}`}>
                    <div className="flex items-center justify-center gap-1">
                            {totalvariance >= 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                            {Math.abs(totalvariance).toFixed(1)}%
                    </div>
                  </td>
                </>
              )}
              {subcounties.map(sc => (
                <td key={sc.id} className="px-6 py-5 text-center border-l">
                  {allStreamData.reduce((s, m) => s + (m[sc.name] || 0), 0).toLocaleString()}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FinancialYearComparisonTable;